/**
 * **The readings behind a low-sleep or low-energy task** — `GET /vitals/customers/{id}/readings` as
 * the plot and the two tiles draw it (attention-queue R27).
 *
 * The rule of R22 — the average of the three newest readings from the last fourteen days — lives on
 * the server and NOWHERE here: the response states the average, how many readings it is taken over,
 * and marks each reading that counts. This module copies those three facts and never derives one of
 * them from the others, so a rule the server changes changes on this card with it.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import type { TrainWithMeModulesVitalsDtosResponsesVitalReadingsResponse as VitalReadingsResponse } from '~/shared/api/generated/model';

export interface VitalReading {
  /** The instant the reading describes — what the server orders the list by. */
  readonly at: Date;
  /** The client's own civil day it was taken on. */
  readonly localDay: string;
  readonly value: number;
  /** The server marked it as one of the readings the average is taken over. */
  readonly counted: boolean;
}

export interface VitalReadings {
  /** Oldest first, as the server sent them. */
  readonly readings: readonly VitalReading[];
  /** The marked ones, in the same order. */
  readonly counted: readonly VitalReading[];
  /** The server's average over the marked readings; `null` when none is recent enough to count. */
  readonly average: number | null;
  /** The newest marked reading — the one the task's day and source belong to. */
  readonly latest: VitalReading | null;
}

export function vitalReadings(response: VitalReadingsResponse | null | undefined): VitalReadings {
  const readings: VitalReading[] = [];
  for (const wire of response?.readings ?? []) {
    const at = wire.effectiveAt === undefined ? null : new Date(wire.effectiveAt);
    // A reading with no instant or no value cannot be placed on the plot, and is not drawn at a guess.
    if (at === null || Number.isNaN(at.getTime()) || typeof wire.value !== 'number') continue;
    readings.push({
      at,
      localDay: wire.localDay ?? '',
      value: wire.value,
      counted: wire.countsTowardAverage === true,
    });
  }
  const counted = readings.filter((reading) => reading.counted);
  return {
    readings,
    counted,
    average: typeof response?.average === 'number' ? response.average : null,
    latest: counted[counted.length - 1] ?? null,
  };
}
